
import React, { useState } from 'react';
import { useForm } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import * as z from 'zod';
import { Button } from '@/components/ui/button';
import { Form, FormControl, FormField, FormItem, FormLabel, FormMessage } from '@/components/ui/form';
import { Textarea } from '@/components/ui/textarea';
import { Input } from '@/components/ui/input';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { useToast } from '@/hooks/use-toast';
import { supabase } from '@/integrations/supabase/client';
import { useAuth } from '@/contexts/AuthContext';
import { formatCurrency } from '@/utils/formatters';
import { Badge } from '@/components/ui/badge';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { useNavigate } from 'react-router-dom';

const paymentRequestSchema = z.object({
  amount: z.number()
    .min(0.01, "Amount must be greater than 0")
    .max(1000000, "Amount cannot exceed 1,000,000"),
  notes: z.string().optional(),
});

type FormData = z.infer<typeof paymentRequestSchema>;

interface PaymentRequestFormProps {
  paymentMethods: any[];
  defaultMethod: any;
  isEligible: boolean;
  threshold: number;
  balance: number;
  isLoadingMethods: boolean;
  onMethodChange: (methodId: string) => void;
  selectedMethodId: string | null;
}

const PaymentRequestForm: React.FC<PaymentRequestFormProps> = ({ 
  paymentMethods,
  defaultMethod,
  isEligible,
  threshold,
  balance,
  isLoadingMethods,
  onMethodChange,
  selectedMethodId
}) => {
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [taxPercentage, setTaxPercentage] = useState(10);
  const { user } = useAuth();
  const { toast } = useToast();
  const navigate = useNavigate();

  const form = useForm<FormData>({
    resolver: zodResolver(paymentRequestSchema),
    defaultValues: {
      amount: balance,
      notes: '',
    },
  });

  // Get the selected payment method
  const selectedPaymentMethod = selectedMethodId 
    ? paymentMethods?.find(pm => pm.id === selectedMethodId)
    : defaultMethod;

  // Calculate tax and net amount based on author's tax percentage
  const calculatePaymentAmounts = (grossAmount: number, taxPercentage: number) => {
    const taxAmount = (grossAmount * taxPercentage) / 100;
    const netAmount = grossAmount - taxAmount;
    return { taxAmount, netAmount };
  };

  React.useEffect(() => {
    const fetchTaxPercentage = async () => {
      if (user?.id) {
        const { data: profile } = await supabase
          .from('profiles')
          .select('tax_percentage')
          .eq('id', user.id)
          .single();
        
        if (profile?.tax_percentage !== undefined) {
          setTaxPercentage(profile.tax_percentage);
        }
      }
    };
    
    fetchTaxPercentage();
  }, [user?.id]);

  const onSubmit = async (data: FormData) => {
    if (!user || !selectedPaymentMethod) {
      toast({
        title: 'Error',
        description: 'Payment method not found. Please set up your payment method first.',
        variant: 'destructive',
      });
      return;
    }

    console.log('PaymentRequestForm: Starting submission');
    setIsSubmitting(true);

    try {
      if (data.amount > balance) {
        toast({
          title: 'Error',
          description: 'Requested amount cannot exceed your available balance.',
          variant: 'destructive',
        });
        return;
      }

      const { taxAmount, netAmount } = calculatePaymentAmounts(data.amount, taxPercentage);

      console.log('PaymentRequestForm: Submitting payment request', {
        author_id: user.id,
        amount: netAmount, // Net amount (what author receives)
        gross_amount: data.amount, // Gross amount (what gets deducted from balance)
        payment_method: selectedPaymentMethod.type,
        notes: data.notes || null
      });

      const { error } = await supabase
        .from('payment_requests')
        .insert({
          author_id: user.id,
          amount: netAmount, // Net amount (what author receives)
          gross_amount: data.amount, // Gross amount (deducted from balance)
          payment_method: selectedPaymentMethod.type,
          notes: data.notes || null,
          status: 'pending',
        });

      if (error) {
        console.error('PaymentRequestForm: Database error', error);
        throw error;
      }

      console.log('PaymentRequestForm: Payment request submitted successfully');

      toast({
        title: 'Payment Request Submitted',
        description: `Your payment request for ${formatCurrency(netAmount)} (after ${taxPercentage}% tax deduction) has been submitted successfully.`,
      });

      form.reset();
      navigate('/author/payments');
    } catch (error) {
      console.error('Error submitting payment request:', error);
      toast({
        title: 'Error',
        description: 'Failed to submit payment request. Please try again.',
        variant: 'destructive',
      });
    } finally {
      setIsSubmitting(false);
    }
  };

  if (!selectedPaymentMethod) {
    return (
      <Card>
        <CardContent className="p-6">
          <p className="text-center text-muted-foreground">
            Please set up a payment method before requesting payment.
          </p>
        </CardContent>
      </Card>
    );
  }

  const amount = form.watch('amount') || 0;
  const { taxAmount, netAmount } = calculatePaymentAmounts(amount, taxPercentage);

  return (
    <Form {...form}>
      <form onSubmit={form.handleSubmit(onSubmit)} className="space-y-6">
        {paymentMethods && paymentMethods.length > 1 && (
          <div className="space-y-2">
            <label className="text-sm font-medium">Payment Method</label>
            <Select value={selectedMethodId || ''} onValueChange={onMethodChange}>
              <SelectTrigger>
                <SelectValue placeholder="Select payment method" />
              </SelectTrigger>
              <SelectContent>
                {paymentMethods.map((method) => (
                  <SelectItem key={method.id} value={method.id}>
                    {method.type.replace('_', ' ').toUpperCase()}
                    {method.is_default && ' (Default)'}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
        )}

        <FormField
          control={form.control}
          name="amount"
          render={({ field }) => (
            <FormItem>
              <FormLabel>Request Amount</FormLabel>
              <FormControl>
                <Input
                  type="number"
                  step="0.01"
                  min={0}
                  max={balance}
                  placeholder="Enter amount to request"
                  {...field}
                  onChange={e => {
                    const value = parseFloat(e.target.value);
                    field.onChange(isNaN(value) ? 0 : value);
                  }}
                />
              </FormControl>
              <FormMessage />
            </FormItem>
          )}
        />

        <Card>
          <CardHeader>
            <CardTitle>Payment Summary</CardTitle>
          </CardHeader>
          <CardContent className="space-y-4">
            <div className="flex justify-between items-center">
              <span>Available Balance:</span>
              <span className="font-medium text-muted-foreground">{formatCurrency(balance)}</span>
            </div>
            <div className="flex justify-between items-center mt-4">
              <span>Request Amount:</span>
              <span className="font-medium">{formatCurrency(amount)}</span>
            </div>
            <div className="flex justify-between items-center">
              <span>Tax ({taxPercentage}%):</span>
              <span className="font-medium text-red-600">-{formatCurrency(taxAmount)}</span>
            </div>
            <div className="border-t pt-2">
              <div className="flex justify-between items-center">
                <span className="font-semibold">Net Amount:</span>
                <span className="font-semibold text-green-600">{formatCurrency(netAmount)}</span>
              </div>
            </div>
            <div className="flex justify-between items-center text-sm text-muted-foreground">
              <span>Payment Method:</span>
              <Badge variant="outline">{selectedPaymentMethod.type.replace('_', ' ').toUpperCase()}</Badge>
            </div>
          </CardContent>
        </Card>

        <FormField
          control={form.control}
          name="notes"
          render={({ field }) => (
            <FormItem>
              <FormLabel>Additional Notes (Optional)</FormLabel>
              <FormControl>
                <Textarea
                  placeholder="Any additional information or special instructions..."
                  {...field}
                />
              </FormControl>
              <FormMessage />
            </FormItem>
          )}
        />

        <div className="flex justify-end gap-4">
          <Button 
            type="button" 
            variant="outline" 
            onClick={() => navigate('/author/payments')}
          >
            Cancel
          </Button>
          <Button 
            type="submit"
            className="bg-royalty-600 hover:bg-royalty-700"
            disabled={!isEligible || isLoadingMethods || isSubmitting}
          >
            {isSubmitting ? 'Submitting...' : 'Submit Request'}
          </Button>
        </div>
      </form>
    </Form>
  );
};

export default PaymentRequestForm;
